import { Account } from 'dashboard/models/account';
import { CaseState } from 'dashboard/models/case-template';
import { Document } from 'dashboard/models/document';
import { GraphQuery } from 'dashboard/models/graph-query';
import { Inquiry } from 'dashboard/models/inquiry';
import { Report } from 'dashboard/models/report';
import { ReportRun } from 'dashboard/models/report-run';
import { SAR } from 'dashboard/models/sar';
import { Transaction } from 'dashboard/models/transaction';
import { TriggerCase } from 'dashboard/models/trigger';
import { Verification } from 'dashboard/models/verification';
import { Workflow } from 'dashboard/models/workflow';
import { FieldsMap } from 'lib/fields';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';

export type CaseObject =
  | Account
  | Document
  | GraphQuery
  | Inquiry
  | Report
  | ReportRun
  | SAR
  | Transaction
  | Verification;

export enum CaseStatus {
  Open = 'open',
  Pending = 'pending',
  Resolved = 'resolved',
}

export type CaseActionWorkflow = {
  workflow: Workflow;
  trigger: TriggerCase;
};

export interface CaseChecklistItem {
  token: string;
  description: string;
  'completed-at': string;
  'required-for': string[];
}

export interface CaseAttachment {
  'byte-size': number;
  'file-type': string;
  filename: string;
  url: string;
  key: string;
}

export interface CaseAttributes {
  status: CaseStatus;
  name: string;
  resolution: string | undefined;
  'checklist-items': CaseChecklistItem[];
  state: CaseState;
  data: {
    metadata: Record<string, any>;
  };
  fields: FieldsMap;
  'case-attachments': CaseAttachment[];

  'created-at': string;
  'updated-at': string;
  'assigned-at'?: string;
  'sla-expires-at'?: string;
  'resolved-at'?: string;
  'redacted-at'?: string;
}

export interface CaseEventAttributes extends CaseAttributes {
  'assignee-id': string;
  'assigner-id': string;
  'assigner-type':
    | ApiModel.CaseAssignmentPolicy
    | ApiModel.Workflow
    | ApiModel.WorkflowRun
    | ApiModel.User;
  'creator-id': string;
  'creator-type': ApiModel.Workflow | ApiModel.WorkflowRun | ApiModel.User;
  'resolver-id': string;
  'resolver-type': ApiModel.Workflow | ApiModel.WorkflowRun | ApiModel.User;
  'updater-id': string;
  'updater-type':
    | ApiModel.CaseAssignmentPolicy
    | ApiModel.Workflow
    | ApiModel.WorkflowRun
    | ApiModel.User;
}

export enum CaseDataManagerObjects {
  // Associations
  Case = 'case', // deprecated since case is a keyword in JS
  CurrentUser = 'current-user', // deprecated since kebab-cased variables can not be used in JS

  // CaseDataManager is at the top-level for backwards compatibility as we migrate to using variables
  CaseDataManager = 'caseDataManager',

  // New variable name keys
  VariableCurrentCase = 'current_case',
  VariableCurrentUser = 'current_user',
}

export type CaseDataManager = Partial<Record<CaseDataManagerObjects, Record<string, any>>>;

export interface CaseMeta {
  'case-data-manager': CaseDataManager;
}

export interface CaseRelationships {
  'case-queue': ApiRelationship<ApiModel.CaseQueue>;
  'case-template': ApiRelationship<ApiModel.CaseTemplate>;
  objects: ApiListRelationship<ApiModel>;
  'case-comments': ApiListRelationship<ApiModel>;
  'case-messages': ApiListRelationship<ApiModel>;
  accounts: ApiListRelationship<ApiModel>;
  tags: ApiListRelationship<ApiModel>;
  creator: ApiRelationship<ApiModel>;
  assignee: ApiRelationship<ApiModel.User>;
  redactor: ApiRelationship<ApiModel.User>;
  resolver: ApiRelationship<ApiModel>;
  updater: ApiRelationship<ApiModel>;
}

export type Case = ApiResource<CaseAttributes, CaseRelationships, ApiModel.Case>;
export type CaseEvent = ApiResource<CaseEventAttributes, CaseRelationships, ApiModel.Case>;
